import {
  IconArrowBack,
  IconCancel,
  IconChevronCompactUp,
  IconChevronLeft,
  IconChevronRight,
  IconChevronUp,
  IconLock,
  IconLockOpen,
  IconRadar,
  IconRadar2,
  IconRotateDot,
  IconRowRemove,
  IconStatusChange,
  IconVideoPlus,
} from "@tabler/icons-react";
// import { AnimatePresence, motion } from "motion/react";
import { usePdarSelection } from "./hooks/usePdars";
import { cn } from "@/client/lib/utils";
import { ButtonGroup } from "@/client/components/shad/button-group";
import { Button } from "@/client/components/shad/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/client/components/shad/collapsible";
import { useCallback, useEffect, useRef, useState } from "react";
import { ScrollArea } from "@/client/components/shad/scroll-area";
import { Slider } from "@/client/components/shad/slider";
import { getHeadingInDegrees } from "@/client/lib/getHeadingInDegrees";
import { useTopic } from "@/hooks/useTopic";
import type { CollapsibleProps } from "@radix-ui/react-collapsible";
import type { Pdars } from "@/client/map/types";
import RippleSpinner from "@/client/components/common/ripple-spinner";

type OrientationSliderProps = {
  pdarId: string;
  // Current wire orientation (0..360) for this PDAR, as last reported by
  // the sim — used to resolve the slider's -180..180 delta into an
  // absolute new bearing before sending the command.
  currentOrientation: number;
  // setOrientationOffset: (pdar_id: string, offsetDeg: number) => void;
  // getOrientationOffset: (pdar_id: string) => number;
  setIsOpen: (open: boolean) => void;
};

// Wraps any degree value into the 0..360 range (e.g. -30 -> 330, 370 -> 10).
function normalizeBearing(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

export const PdarStations = () => {
  const {
    pdars,
    selectedPdarIds,
    select,
    deselect,
    setOrientationOffset,
    getOrientationOffset,
  } = usePdarSelection();

  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = useCallback(
    (pdar_id: string) => {
      if (selectedPdarIds.includes(pdar_id)) {
        deselect(pdar_id);
        return;
      }

      select(pdar_id);
    },
    [selectedPdarIds],
  );

  return (
    <div className="grid gap-3">
      {pdars.map((item) => {
        // const isSelected = selectedPdarIds.includes(item.pdar_id);

        return <OrientationItem key={item.pdar_id} item={item} />;
      })}
    </div>
  );
};

const OrientationItem = ({ item }: { item: Pdars }) => {
  const [isOpen, setIsOpen] = useState(false);

  const { selectedPdarIds, select, deselect, setOrientationOffset } =
    usePdarSelection();
  const isSelected = selectedPdarIds.includes(item.pdar_id);
  const lastOrientationRef = useRef<number | null>(null);

  useEffect(() => {
    if (lastOrientationRef.current === null) {
      lastOrientationRef.current = item.orientation;
      return;
    }

    if (lastOrientationRef.current !== item.orientation) {
      lastOrientationRef.current = item.orientation;
      setOrientationOffset(item.pdar_id, 0);
    }
  }, [item.orientation, item.pdar_id, setOrientationOffset]);

  const handleSelectToggle = () => {
    if (isSelected) {
      deselect(item.pdar_id);
      return;
    }

    select(item.pdar_id);
  };

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="grid gap-4 h-full"
    >
      <div className="grid gap-3 pb-6 border-b border-dashed border-zinc-700">
        <div className="flex items-center cursor-pointer duration-300">
          <div
            onClick={handleSelectToggle}
            className={cn(
              "relative grow flex items-center gap-2 hover:text-amber-100",
              isSelected && "text-amber-100",
            )}
          >
            <IconRadar2 className="text-muted-foreground" />
            <div className="flex flex-col items-start">
              <span className="text-sm font-semibold leading-tight">
                {item.pdar_id}
              </span>
              <span className="text-muted-foreground text-xs leading-tight capitalize">
                {item.health_state}
              </span>
            </div>
            <span className="absolute left-3.5 bottom-0 size-3 rounded-full border-2 border-card bg-lime-400 saturate-50" />
          </div>
          <div>
            <CollapsibleTrigger asChild disabled={isOpen}>
              <Button variant="outline" size="icon-sm">
                <IconRadar />
              </Button>
            </CollapsibleTrigger>
          </div>
        </div>

        <div
          className={cn(
            "grid gap-2 text-xs duration-200 text-muted-foreground/90",
            isSelected && "text-white",
          )}
        >
          <dl className="grid grid-cols-2 px-2 py-1 bg-sidebar-accent/50 rounded-md">
            <dt>Temp</dt>
            <dd>{item.ambient_temp_c}</dd>
          </dl>
          <dl className="grid grid-cols-2 px-2 py-1 bg-sidebar-accent/50 rounded-md">
            <dt>Power</dt>
            <dd>{item.power_w}w </dd>
          </dl>

          <dl className="grid grid-cols-2 px-2 py-1 bg-sidebar-accent/50 rounded-md">
            <dt>Orientation</dt>
            <dd>{item.orientation.toFixed(2)}°</dd>
          </dl>
        </div>

        <CollapsibleContent>
          <OrientationSlider
            setIsOpen={(open) => setIsOpen(open)}
            pdarId={item.pdar_id}
            currentOrientation={item.orientation}
          />
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

const OrientationSlider = ({
  pdarId,
  currentOrientation,
  // setOrientationOffset,
  // getOrientationOffset,
  setIsOpen,
}: OrientationSliderProps) => {
  const { sendCommand } = useTopic<unknown>("pdar/offset");
  const { setOrientationOffset } = usePdarSelection();

  const [committing, setCommitting] = useState(false);
  const [value, setValue] = useState(0);

  const handleChange = (value: number[]) => {
    const next = value[0] ?? 0;
    setOrientationOffset(pdarId, next);
    setValue(next);
  };

  const handleCommit = () => {
    // The sim expects the fully-resolved new orientation (0..360), not a
    // delta — so add the slider's -180..180 value onto the PDAR's current
    // wire orientation and wrap it here, once, before sending.
    const newOrientation = normalizeBearing(currentOrientation + value);

    sendCommand("pdar/offset", {
      pdar_id: pdarId,
      offset: newOrientation,
    });

    setOrientationOffset(pdarId, value ?? 0);

    setCommitting(true);

    setTimeout(() => {
      setOrientationOffset(pdarId, 0);
      setCommitting(false);
      setIsOpen(false);
    }, 1000);
  };

  const increament = () => {
    const adjustedValue = value + 0.1;
    setOrientationOffset(pdarId, adjustedValue);
    setValue(adjustedValue);
  };

  const decreament = () => {
    const adjustedValue = value - 0.1;
    setOrientationOffset(pdarId, adjustedValue);
    setValue(adjustedValue);
  };

  const cancel = () => {
    setOrientationOffset(pdarId, 0);
    setIsOpen(false);
  };

  if (committing) {
    return (
      <div className="bg-background border rounded-md p-3 min-h-26 mb-1 flex flex-col items-center justify-center">
        <RippleSpinner className="size-8 text-lime-500" />
        <p className="text-xs font-mono text-muted-foreground">Working...</p>
      </div>
    );
  }

  return (
    <div className="bg-background border rounded-md p-3 grid gap-2">
      <div className="flex items-end -mb-2 pt-1">
        <div className="text-left text-muted-foreground font-mono">-180°</div>
        <div className="grow text-center">{value.toFixed(2)}°</div>
        <div className="text-right text-muted-foreground font-mono">180°</div>
      </div>
      <Slider
        defaultValue={[value]}
        onValueChange={handleChange}
        // onValueCommit={handleCommit}
        max={180}
        min={-180}
        step={0.01}
        className="mx-auto py-3 w-full max-w-xs"
      />
      <div className="flex items-center">
        <div className="grow">
          <ButtonGroup>
            <Button
              onClick={decreament}
              variant="outline"
              size="icon-xs"
              className="p-3"
            >
              <IconChevronLeft />
            </Button>
            <Button
              onClick={increament}
              variant="outline"
              size="icon-xs"
              className="p-3"
            >
              <IconChevronRight />
            </Button>
          </ButtonGroup>
        </div>
        <div>
          <ButtonGroup>
            <Button onClick={cancel} variant="outline" size="xs">
              <IconCancel />
            </Button>
            <Button onClick={handleCommit} variant="outline" size="xs">
              <span className="text-xs font-light">Apply</span>
            </Button>
          </ButtonGroup>
        </div>
      </div>
    </div>
  );
};
